import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { api, money } from "../lib/api";

const COLORS = ["#0f766e", "#3484a3", "#e6a44e", "#c75451", "#6a5b93"];

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics").then((r) => setData(r.data)).catch(() => setData({ stock_mix: [], department_consumption: [], top_vendors: [], low_stock: [], expiring: [] }));
  }, []);

  if (!data) return <div className="empty">Loading analytics…</div>;

  return (
    <div className="analytics-grid">
      <section className="panel chart-panel">
        <div className="panel-head">
          <div>
            <p className="eyebrow">CONSUMPTION</p>
            <h3>Department indents raised</h3>
          </div>
        </div>
        <div style={{ width: "100%", height: 250 }}>
          <ResponsiveContainer>
            <BarChart data={data.department_consumption} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e6ede9" />
              <XAxis dataKey="department" tick={{ fontSize: 11, fill: "#4a6764" }} />
              <YAxis tick={{ fontSize: 11, fill: "#4a6764" }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <Bar dataKey="indents" fill="#0f766e" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="panel breakdown">
        <div className="panel-head">
          <div>
            <p className="eyebrow">STOCK MIX</p>
            <h3>By category (₹ value)</h3>
          </div>
        </div>
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={data.stock_mix} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={3}>
                {data.stock_mix.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => money(v)} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="legend-inline">
          {data.stock_mix.map((s, i) => (
            <span key={s.name}><i className="dot" style={{ background: COLORS[i % COLORS.length] }} />{s.name} · {s.pct}%</span>
          ))}
        </div>
      </section>

      <section className="panel expiry-panel">
        <div className="panel-head">
          <div>
            <p className="eyebrow">EXPIRY WATCH</p>
            <h3>Batches nearing expiry</h3>
          </div>
        </div>
        {data.expiring.length === 0 && <div className="empty">Nothing expiring soon.</div>}
        {data.expiring.slice(0, 6).map((x, i) => (
          <div className="expiry-row" key={i}>
            <div className="medicine-avatar">Rx</div>
            <div className="row-copy">
              <strong>{x.name}</strong>
              <span>Batch {x.batch} · {x.stock} in stock</span>
            </div>
            <span className={`expiry-date ${x.days_left < 30 ? "danger" : ""}`}>
              {x.expiry}<small>{x.days_left < 0 ? "Expired" : `${x.days_left} days left`}</small>
            </span>
          </div>
        ))}
      </section>

      <section className="panel low-stock-panel">
        <div className="panel-head">
          <div>
            <p className="eyebrow">LOW STOCK</p>
            <h3>Reorder now</h3>
          </div>
        </div>
        {data.low_stock.length === 0 && <div className="empty">All items are above reorder level.</div>}
        {data.low_stock.slice(0, 6).map((x, i) => (
          <div className="stock-row" key={i}>
            <div className="medicine-avatar amber">!</div>
            <div className="row-copy">
              <strong>{x.name}</strong>
              <span>Reorder at {x.reorder_level} {x.unit}</span>
            </div>
            <div className="stock-number">
              <strong>{x.stock}</strong>
              <span>in stock</span>
            </div>
          </div>
        ))}
      </section>

      <section className="panel vendor-panel">
        <div className="panel-head">
          <div>
            <p className="eyebrow">TOP VENDORS</p>
            <h3>By purchase value</h3>
          </div>
        </div>
        {data.top_vendors.length === 0 && <div className="empty">No purchases yet.</div>}
        {data.top_vendors.slice(0, 6).map((x, i) => (
          <div className="stock-row" key={i}>
            <div className="medicine-avatar teal">{i + 1}</div>
            <div className="row-copy">
              <strong>{x.vendor}</strong>
              <span>Purchase spend</span>
            </div>
            <div className="stock-number">
              <strong>{money(x.value)}</strong>
              <span>invoiced</span>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
